import { useState, useEffect, useCallback, useRef } from 'react';

interface UseInactivityTimerConfig {
    warningThreshold: number; // milliseconds before showing warning
    logoutThreshold: number;  // milliseconds before auto-logout
    onWarning?: () => void;   // callback when warning should be shown
    onLogout?: () => void;    // callback when user should be logged out
}

interface UseInactivityTimerReturn {
    isWarningShown: boolean;
    remainingTime: number; // seconds until logout
    resetTimer: () => void;
    pauseTimer: () => void;
    resumeTimer: () => void;
}

const STORAGE_KEY_ACTIVITY = 'lastActivity';
const STORAGE_KEY_LOGOUT = 'forceLogout';
const ACTIVITY_DEBOUNCE = 1000; // 1 second debounce

export const useInactivityTimer = ({
    warningThreshold = 28 * 60 * 1000, // 28 minutes
    logoutThreshold = 30 * 60 * 1000,   // 30 minutes
    onWarning,
    onLogout
}: UseInactivityTimerConfig): UseInactivityTimerReturn => {
    const [isWarningShown, setIsWarningShown] = useState(false);
    const [remainingTime, setRemainingTime] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const lastActivityRef = useRef<number>(Date.now());
    const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
    const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Clear all timers
    const clearTimers = useCallback(() => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    }, []);

    // Update localStorage with last activity timestamp
    const updateLastActivity = useCallback(() => {
        const now = Date.now();
        lastActivityRef.current = now;
        localStorage.setItem(STORAGE_KEY_ACTIVITY, now.toString());
    }, []);

    // Start countdown when warning is shown
    const startCountdown = useCallback(() => {
        const timeUntilLogout = logoutThreshold - warningThreshold; // 2 minutes
        setRemainingTime(Math.floor(timeUntilLogout / 1000));

        countdownIntervalRef.current = setInterval(() => {
            setRemainingTime((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current!);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [warningThreshold, logoutThreshold]);

    // Reset the inactivity timer
    const resetTimer = useCallback(() => {
        if (isPaused) return;

        clearTimers();
        updateLastActivity();
        setIsWarningShown(false);
        setRemainingTime(0);

        // Set warning timer
        warningTimerRef.current = setTimeout(() => {
            setIsWarningShown(true);
            startCountdown();
            onWarning?.();
        }, warningThreshold);

        // Set logout timer
        logoutTimerRef.current = setTimeout(() => {
            onLogout?.();
        }, logoutThreshold);
    }, [isPaused, clearTimers, updateLastActivity, startCountdown, onWarning, onLogout, warningThreshold, logoutThreshold]);

    // Handle activity events with debouncing
    const handleActivity = useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            resetTimer();
        }, ACTIVITY_DEBOUNCE);
    }, [resetTimer]);

    // Pause the timer (e.g., when modal is open)
    const pauseTimer = useCallback(() => {
        setIsPaused(true);
        clearTimers();
    }, [clearTimers]);

    // Resume the timer
    const resumeTimer = useCallback(() => {
        setIsPaused(false);
        resetTimer();
    }, [resetTimer]);

    // Listen for storage events (multi-tab synchronization)
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            // Handle activity sync from other tabs
            if (e.key === STORAGE_KEY_ACTIVITY && e.newValue) {
                const lastActivity = parseInt(e.newValue, 10);
                const timeSinceActivity = Date.now() - lastActivity;

                // If activity happened recently in another tab, reset our timer
                if (timeSinceActivity < ACTIVITY_DEBOUNCE) {
                    resetTimer();
                }
            }

            // Handle force logout from other tabs
            if (e.key === STORAGE_KEY_LOGOUT && e.newValue) {
                const logoutTime = parseInt(e.newValue, 10);
                const timeSinceLogout = Date.now() - logoutTime;

                // If logout happened recently in another tab, logout here too
                if (timeSinceLogout < 5000) { // Within 5 seconds
                    onLogout?.();
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [resetTimer, onLogout]);

    // Initialize timer on mount only
    useEffect(() => {
        resetTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Setup activity listeners
    useEffect(() => {
        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

        activityEvents.forEach((event) => {
            window.addEventListener(event, handleActivity);
        });

        // Cleanup listeners only (don't clear timers here)
        return () => {
            activityEvents.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });
        };
    }, [handleActivity]);

    // Cleanup timers and debounce on unmount
    useEffect(() => {
        return () => {
            clearTimers();
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [clearTimers]);

    // Trigger logout when countdown reaches 0
    useEffect(() => {
        if (remainingTime === 0 && isWarningShown) {
            // Write force logout to localStorage to trigger logout in all tabs
            localStorage.setItem(STORAGE_KEY_LOGOUT, Date.now().toString());
            onLogout?.();
        }
    }, [remainingTime, isWarningShown, onLogout]);

    return {
        isWarningShown,
        remainingTime,
        resetTimer,
        pauseTimer,
        resumeTimer
    };
};
